/**
 * Text Processing Pipeline (React/TypeScript port)
 *
 * Phases:
 *   1. EXTRACT   – hide special tokens from markdown processing
 *   2. MARKDOWN  – regex-based markdown conversion (no external library)
 *   3. RESTORE   – replace placeholders with final HTML (reverse order)
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface TocEntry {
  level: number;
  text: string;
  id: string;
}

interface PipelineContext {
  codeBlocks: string[];
  cards: string[];
  badges: string[];
  mana: string[];
  toc: TocEntry[];
  /** Heading slugs already used in this document → times seen, so repeated headings get `-2`, `-3`… */
  slugCounts: Map<string, number>;
}

// ── HTML escaping ────────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Escape the characters that open markup in text content. Applied to the whole document after the
 *  special tokens are extracted, so owner-typed text (change notes) can never inject a tag. */
function escapeText(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Inverse of `escapeText`, for plain-text consumers (TOC entries render as React text). */
function unescapeText(str: string): string {
  return str.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

/** Only absolute http(s) URLs become links; `javascript:`, `data:` and the rest render as text. */
function isSafeLinkUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

// ── Mana symbol helpers ──────────────────────────────────────────────────────

const SYMBOL_TITLES: Record<string, string> = {
  W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green',
  C: 'Colorless', X: 'X', T: 'Tap', S: 'Snow', E: 'Energy',
};

// Mana font uses different class names for some symbols (ms-tap, ms-untap, etc.)
const MANA_CLASS_OVERRIDES: Record<string, string> = {
  T: 'ms-tap',
  Q: 'ms-untap',
};

function manaClass(raw: string): string {
  if (raw.includes('/')) {
    const parts = raw.split('/');
    return 'ms-' + parts.join('').toLowerCase();
  }
  return MANA_CLASS_OVERRIDES[raw.toUpperCase()] ?? 'ms-' + raw.toLowerCase();
}

function manaTitle(raw: string): string {
  if (raw.includes('/')) {
    const parts = raw.split('/');
    if (parts[1] === 'P') return (SYMBOL_TITLES[parts[0]] || parts[0]) + ' Phyrexian';
    return (SYMBOL_TITLES[parts[0]] || parts[0]) + '/' + (SYMBOL_TITLES[parts[1]] || parts[1]);
  }
  if (/^\d+$/.test(raw)) return raw + ' Generic';
  return SYMBOL_TITLES[raw] || raw;
}

// ── Slug helper for TOC ids ──────────────────────────────────────────────────

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

/** A heading id unique within one document: the second `## History` is `history-2` (TOC keys on it). */
function uniqueSlug(text: string, ctx: PipelineContext): string {
  const base = slugify(text);
  const n = (ctx.slugCounts.get(base) ?? 0) + 1;
  ctx.slugCounts.set(base, n);
  return n === 1 ? base : `${base}-${n}`;
}

// ── Phase 1: Extract ─────────────────────────────────────────────────────────

function extractCodeBlocks(text: string, ctx: PipelineContext): string {
  // Fenced code blocks
  text = text.replace(/(```[\s\S]*?```)/g, (match) => {
    const ph = `@@CODE_${ctx.codeBlocks.length}@@`;
    ctx.codeBlocks.push(match);
    return ph;
  });
  // Inline code
  text = text.replace(/(`[^`\n]+`)/g, (match) => {
    const ph = `@@CODE_${ctx.codeBlocks.length}@@`;
    ctx.codeBlocks.push(match);
    return ph;
  });
  return text;
}

function extractCards(text: string, ctx: PipelineContext): string {
  return text.replace(/\[\[([^\]]+)\]\]/g, (_, name: string) => {
    const ph = `@@CARD_${ctx.cards.length}@@`;
    ctx.cards.push(name);
    return ph;
  });
}

function extractBadges(text: string, ctx: PipelineContext): string {
  return text.replace(/\[(GAME CHANGER|BANNED|UNRELEASED)\]/g, (_, kind: string) => {
    const ph = `@@BADGE_${ctx.badges.length}@@`;
    ctx.badges.push(kind);
    return ph;
  });
}

function extractMana(text: string, ctx: PipelineContext): string {
  return text.replace(/\{([WUBRGCXTSEP](?:\/[WUBRGCP2])?|\d{1,2}(?:\/[WUBRGC])?)\}/gi, (_, sym: string) => {
    const upper = sym.toUpperCase();
    const ph = `@@MANA_${ctx.mana.length}@@`;
    ctx.mana.push(upper);
    return ph;
  });
}

// ── Phase 2: Markdown ────────────────────────────────────────────────────────

function processMarkdown(text: string, ctx: PipelineContext): string {
  // First, restore code blocks so they render properly. The document was escaped while they were
  // hidden: fenced blocks are escaped as a whole when emitted below, inline code is escaped here.
  for (let i = 0; i < ctx.codeBlocks.length; i++) {
    const block = ctx.codeBlocks[i];
    text = text.split(`@@CODE_${i}@@`).join(block.startsWith('```') ? block : escapeText(block));
  }

  const lines = text.split('\n');
  const output: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let inCodeBlock = false;
  let codeContent: string[] = [];
  let paragraphLines: string[] = [];
  let tableRows: string[] = [];
  let quoteLines: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      const content = paragraphLines.join(' ');
      output.push(`<p>${processInline(content)}</p>`);
      paragraphLines = [];
    }
  };

  // Consecutive `> ` lines → one <blockquote>; an empty `>` line separates paragraphs inside it.
  const flushQuote = () => {
    if (quoteLines.length === 0) return;
    const paras: string[][] = [[]];
    for (const ln of quoteLines) {
      if (ln) paras[paras.length - 1].push(ln);
      else if (paras[paras.length - 1].length) paras.push([]);
    }
    const inner = paras.filter((p) => p.length).map((p) => `<p>${processInline(p.join(' '))}</p>`).join('');
    output.push(`<blockquote>${inner}</blockquote>`);
    quoteLines = [];
  };

  const flushList = () => {
    if (inList) {
      output.push(`</${inList}>`);
      inList = null;
    }
  };

  const flushTable = () => {
    if (tableRows.length < 2) {
      // Not enough rows for a valid table — treat as regular text
      for (const row of tableRows) paragraphLines.push(row);
      tableRows = [];
      return;
    }
    // Row 0 = header, row 1 = separator (skip), rows 2+ = body
    const parseCells = (row: string) =>
      row.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
    const headers = parseCells(tableRows[0]);
    const bodyRows = tableRows.slice(2); // skip separator row
    let html = '<div class="overflow-x-auto"><table class="w-full text-sm border-collapse my-2">';
    html += '<thead><tr>';
    for (const h of headers) html += `<th class="text-left px-2 py-1.5 border-b border-outline-variant font-semibold text-on-surface">${processInline(h)}</th>`;
    html += '</tr></thead><tbody>';
    for (const row of bodyRows) {
      const cells = parseCells(row);
      html += '<tr>';
      for (const cell of cells) html += `<td class="px-2 py-1.5 border-b border-outline-variant/50 text-on-surface-variant">${processInline(cell)}</td>`;
      html += '</tr>';
    }
    html += '</tbody></table></div>';
    output.push(html);
    tableRows = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Fenced code blocks
    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        output.push(`<pre><code>${escapeHtml(codeContent.join('\n'))}</code></pre>`);
        codeContent = [];
        inCodeBlock = false;
      } else {
        // Start code block
        flushParagraph();
        flushQuote();
        flushList();
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeContent.push(line);
      continue;
    }

    // Blockquote lines. The document is escaped by now, so the marker reads `&gt;`.
    const quoteMatch = trimmed.match(/^&gt;(?:\s(.*))?$/);
    if (quoteMatch && tableRows.length === 0) {
      flushParagraph();
      flushList();
      quoteLines.push((quoteMatch[1] ?? '').trim());
      continue;
    }
    flushQuote();

    // Empty line
    if (!trimmed) {
      flushParagraph();
      flushTable();
      continue;
    }

    // Table rows (lines starting and containing |)
    if (trimmed.startsWith('|') || (tableRows.length > 0 && trimmed.includes('|'))) {
      flushParagraph();
      flushList();
      tableRows.push(trimmed);
      continue;
    }
    // Non-table line after table rows — flush the table first
    if (tableRows.length > 0) {
      flushTable();
    }

    // Horizontal rules
    if (/^([-*_])\1{2,}\s*$/.test(trimmed)) {
      flushParagraph();
      flushList();
      output.push('<hr />');
      continue;
    }

    // Headers
    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushParagraph();
      flushList();
      const level = headerMatch[1].length;
      const rawText = headerMatch[2];
      const plain = unescapeText(rawText.replace(/@@\w+_\d+@@/g, ''));
      const id = uniqueSlug(plain, ctx);
      ctx.toc.push({ level, text: plain.replace(/\*\*/g, ''), id });
      const tag = `h${level}`;
      output.push(`<${tag} id="${id}">${processInline(rawText)}</${tag}>`);
      continue;
    }

    // Bullet list items
    const bulletMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      flushParagraph();
      if (inList !== 'ul') {
        flushList();
        inList = 'ul';
        output.push('<ul>');
      }
      output.push(`<li>${processInline(bulletMatch[1])}</li>`);
      continue;
    }

    // Numbered list items
    const numberedMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (numberedMatch) {
      flushParagraph();
      if (inList !== 'ol') {
        flushList();
        inList = 'ol';
        output.push('<ol>');
      }
      output.push(`<li>${processInline(numberedMatch[1])}</li>`);
      continue;
    }

    // Regular text — accumulate for paragraph
    flushList();
    paragraphLines.push(trimmed);
  }

  // Flush remaining
  if (inCodeBlock) {
    output.push(`<pre><code>${escapeHtml(codeContent.join('\n'))}</code></pre>`);
  }
  flushTable();
  flushParagraph();
  flushQuote();
  flushList();

  return output.join('\n');
}

/** Process inline formatting: **bold**, *italic*, inline `code`, [links](url) */
function processInline(text: string): string {
  // Inline code
  text = text.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  // Bold
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic (but not inside **)
  text = text.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  // Markdown links [text](url) — but not [[Card Name]] which is already extracted. Only http(s)
  // URLs link; any other scheme (javascript:, data:) is left as the literal markdown text.
  text = text.replace(/(?<!\[)\[([^\]]+)\]\(([^)]+)\)/g, (match, label: string, url: string) =>
    isSafeLinkUrl(url)
      ? `<a href="${url.trim().replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${label}</a>`
      : match);
  return text;
}

// ── Phase 3: Restore ─────────────────────────────────────────────────────────

function restoreMana(text: string, ctx: PipelineContext): string {
  for (let i = 0; i < ctx.mana.length; i++) {
    const sym = ctx.mana[i];
    const icon = `<i class="ms ${manaClass(sym)} ms-cost mana-symbol" title="${manaTitle(sym)}"></i>`;
    text = text.split(`@@MANA_${i}@@`).join(icon);
  }
  return text;
}

function restoreBadges(text: string, ctx: PipelineContext): string {
  for (let i = 0; i < ctx.badges.length; i++) {
    const kind = ctx.badges[i];
    let html: string;
    if (kind === 'GAME CHANGER') {
      html = '<span class="dlw-indicators"><span class="dlw-badge-gc" title="Game Changer (bracket-restricted)">GC</span></span>';
    } else if (kind === 'UNRELEASED') {
      html = '<span class="dlw-indicators"><span class="dlw-badge-unreleased" title="Card not yet released">Unreleased</span></span>';
    } else {
      html = '<span class="dlw-indicators"><span class="dlw-badge-banned" title="Banned in Commander">B</span></span>';
    }
    text = text.split(`@@BADGE_${i}@@`).join(html);
  }
  return text;
}

function restoreCards(text: string, ctx: PipelineContext): string {
  for (let i = 0; i < ctx.cards.length; i++) {
    const name = ctx.cards[i];
    const escapedAttr = escapeHtml(name);
    const tooltip = `<span class="decklist-card" data-card-name="${escapedAttr}" tabindex="0" style="cursor:help;color:var(--primary);">${escapedAttr}</span>`;
    text = text.split(`@@CARD_${i}@@`).join(tooltip);
  }
  return text;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Process analysis text through the full pipeline.
 * Returns rendered HTML and a table-of-contents array.
 */
/**
 * Convert mana cost strings like "{2}{U}{B}" into mana font HTML icons.
 * Safe to call on any text — only `{X}` tokens are replaced.
 */
export function renderManaSymbols(text: string): string {
  return text.replace(/\{([WUBRGCXTSEP](?:\/[WUBRGCP2])?|\d{1,2}(?:\/[WUBRGC])?)\}/gi, (_, sym: string) => {
    const upper = sym.toUpperCase();
    const cls = manaClass(upper);
    return `<i class="ms ${cls} ms-cost mana-symbol" title="${manaTitle(upper)}"></i>`;
  });
}

export function processAnalysisText(text: string): { html: string; toc: TocEntry[] } {
  if (!text) return { html: '<p>No analysis available</p>', toc: [] };

  const ctx: PipelineContext = {
    codeBlocks: [],
    cards: [],
    badges: [],
    mana: [],
    toc: [],
    slugCounts: new Map(),
  };

  // Phase 0: Unescape literal \uXXXX sequences the LLM may emit
  text = text.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );

  // Phase 1: Extract (protect special tokens from markdown)
  text = extractCodeBlocks(text, ctx);
  text = extractCards(text, ctx);
  text = extractBadges(text, ctx);
  text = extractMana(text, ctx);

  // Phase 1b: Escape. Everything left is plain text (owner notes included), so `<`, `>` and `&`
  // can never open markup. The extracted tokens are placeholders and are escaped on restore.
  text = escapeText(text);

  // Phase 2: Markdown
  text = processMarkdown(text, ctx);

  // Phase 3: Restore (reverse order — mana, badges, cards)
  text = restoreMana(text, ctx);
  text = restoreBadges(text, ctx);
  text = restoreCards(text, ctx);

  // Safety net: convert any mana symbols that escaped Phase 1 extraction
  // (e.g. inside LLM-generated backtick code spans like `{2}{W}{U}`)
  text = renderManaSymbols(text);

  return { html: text, toc: ctx.toc };
}
